// The delivery process, canonical in docs/product/policies/delivery-process.md.
//
// This is the *delivery* axis — how work executes. The *commercial* axis — how
// a client buys — is product-positioning.md §3.1 and deliberately lives
// nowhere near here. Nothing in this file should be reworded into a menu of
// things to buy.
//
// One definition, rendered once, on every service page. Per delivery-process.md
// §3.1 this must never become a field on ServiceRecord: fifteen near-identical
// copies is fifteen chances to drift into fifteen slightly different claims
// about how the company works.

export type DeliveryPhaseId =
  "study" | "initial-design" | "detailed-design" | "implementation";

export type DeliveryPhase = {
  id: DeliveryPhaseId;
  // Displayed as the step marker. Zero-based because Phase 0 is frequently the
  // whole engagement, and numbering it 1 implies it is a preliminary rather
  // than a deliverable (delivery-process.md §2).
  step: number;
  name: string;
  // Always visible. The section has to read correctly with every detail panel
  // closed — see delivery-process.md §4.3.
  summary: string;
  // Standard deliverables produced in this phase across all projects.
  produces: string[];
  // Revealed on demand. Detail, never the load-bearing content.
  work: string[];
};

export const deliveryProcess = {
  title: "How the Work Runs",
  subheading:
    "The same four phases on every engagement. What changes per service is what Phase 0 inspects and what Phase 3 ships — not the sequence.",
  // Copy is deliberately process-only. No counts, no scale, no delivery
  // record: see delivery-process.md §5.1 for why that boundary is load-bearing
  // rather than stylistic.
  phases: [
    {
      id: "study",
      step: 0,
      name: "Study",
      summary:
        "Intake and inspection. What exists, what state it is in, and what the real constraint is as opposed to the stated one.",
      produces: ["Spikes", "Whitepapers"],
      work: [
        "System and corpus inventory",
        "Dependency and integration mapping",
        "Source-quality assessment",
        "Review of the standards and business rules actually in force",
        "How the current system is used, and where people work around it",
        "Risk identification",
      ],
    },
    {
      id: "initial-design",
      step: 1,
      name: "Initial Design",
      summary:
        "Frameworks and architecture. The load-bearing decisions, made explicitly and written down rather than settled by accident.",
      produces: [
        "Drawings",
        "Standards",
        "Procedures",
        "Base repo(s)",
        "Base CI/CD",
        "Dev deployment",
      ],
      work: [
        "Component and service boundaries",
        "The intermediate models that decouple source from delivery target",
        "Data and integration contracts",
        "Technology selection against the constraints found in Phase 0",
        "Delivery sequencing",
        "Which decisions are expensive to revisit later",
      ],
    },
    {
      id: "detailed-design",
      step: 2,
      name: "Detailed Design",
      summary:
        "Code, pipelines, and delivery apparatus. Design continues in the implementation rather than in separate documents.",
      produces: [
        "Code implementation",
        "Unit tests",
        "QA procedures",
        "Detailed documentation",
        "QA deployment",
      ],
      work: [
        "Implementation of the Phase 1 architecture",
        "Test strategy, and the tests themselves",
        "CI/CD, environment and configuration management",
        "Observability",
        "Validation suites that exercise behaviour, not only schema conformance",
        "Documentation sufficient for another team to maintain it",
      ],
    },
    {
      id: "implementation",
      step: 3,
      name: "Implementation",
      summary:
        "Continuous deployment of code, and the operational reality of running and maintaining software with real users.",
      produces: ["Prod deployment", "QA", "Testing", "As-builts"],
      work: [
        "Release and rollback paths",
        "Packaging and distribution where the target environment requires it",
        "Update mechanics",
        "Handover",
        "The iteration that only becomes possible with real users in front of the system",
      ],
    },
  ] satisfies DeliveryPhase[],
} as const;
